import { useEffect } from "react";
import { useLocation } from "react-router-dom";

const BASE_URL = "https://sporty-contract-coach.lovable.app";

/**
 * Normalizes a URL path to its canonical form:
 * - Removes trailing slashes (except for root)
 * - Converts to lowercase
 * - Removes query strings and hash fragments
 */
function normalizeCanonicalPath(pathname: string): string {
  // Start with the pathname only (no query or hash)
  let normalized = pathname;
  
  // Convert to lowercase for consistency
  normalized = normalized.toLowerCase();
  
  // Remove trailing slash (except for root path)
  if (normalized !== "/" && normalized.endsWith("/")) {
    normalized = normalized.slice(0, -1);
  }
  
  // Handle root path
  if (normalized === "/") {
    return "";
  }
  
  return normalized;
}

/**
 * Dynamically updates the canonical URL meta tag based on current route.
 * This prevents "Alternate page with proper canonical tag" errors in Search Console
 * by ensuring all URL variations (trailing slashes, query params, case differences)
 * point to a single canonical version.
 */
export const DynamicHead = () => {
  const location = useLocation();

  useEffect(() => {
    // Build normalized canonical URL
    const normalizedPath = normalizeCanonicalPath(location.pathname);
    const canonicalUrl = `${BASE_URL}${normalizedPath}`;

    // Update or create canonical link
    let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
    if (canonicalLink) {
      canonicalLink.href = canonicalUrl;
    } else {
      canonicalLink = document.createElement("link");
      canonicalLink.rel = "canonical";
      canonicalLink.href = canonicalUrl;
      document.head.appendChild(canonicalLink);
    }

    // Update og:url meta tag
    let ogUrl = document.querySelector('meta[property="og:url"]') as HTMLMetaElement;
    if (ogUrl) {
      ogUrl.content = canonicalUrl;
    }

    // Update twitter:url meta tag
    let twitterUrl = document.querySelector('meta[name="twitter:url"]') as HTMLMetaElement;
    if (twitterUrl) {
      twitterUrl.content = canonicalUrl;
    }

    // Redirect to canonical URL if current URL differs (removes trailing slashes, normalizes case)
    const currentPath = location.pathname;
    if (currentPath !== "/" && currentPath !== normalizedPath && currentPath.toLowerCase() === normalizedPath) {
      // Only redirect for case/trailing slash normalization, not for completely different paths
      window.history.replaceState(null, "", normalizedPath + location.search + location.hash);
    }
  }, [location.pathname, location.search, location.hash]);

  return null;
};

export default DynamicHead;

